import contentOutputs from '../editorCapabilities/ContentOutputTypes';

import * as SnippetsApi from '../commands/snippets/draft-content-transplant-snippets/SnippetsApi';
import transformSnippetsData from '../commands/snippets/draft-content-transplant-snippets/transformSnippetsData';
import SnippetsPopoverEmptyState from '../commands/snippets/draft-content-transplant-snippets/SnippetsPopoverEmptyState';
import SnippetsPopoverErrorState from '../commands/snippets/draft-content-transplant-snippets/SnippetsPopoverErrorState';
import { getNewSnippetsUrl, getSnippetsUrl } from '../utils/manageLinks';

import { quotesApiWrapper } from '../commands/quotes/QuotesApi';
import QuotesPopoverEmptyState from '../commands/quotes/QuotesPopoverEmptyState';
import GenericPopoverErrorState from '../components/GenericPopoverErrorState';
import { getFullUrl } from 'hubspot-url-utils';

import { templatesApiWrapper } from '../commands/templates/TemplatesApi';
import TemplatesPopoverEmptyState, {
  getTemplatesUrl,
} from '../commands/templates/TemplatesPopoverEmptyState';
import TemplatesPopoverErrorState from '../commands/templates/TemplatesPopoverErrorState';

import KnowledgeArticleRenderer from './KnowledgeArticleRenderer';

import * as InvoicesApi from '../commands/invoices/InvoicesApi';
import transformInvoicesData from '../commands/invoices/transformInvoicesData';
import InvoicesPopoverEmptyState from '../commands/invoices/InvoicesPopoverEmptyState';
import InvoicesPopoverErrorState from '../commands/invoices/InvoicesPopoverErrorState';
import { getInvoicesUrl } from '../commands/invoices/InsertInvoices';
import { Tracker } from '../../utils/usageTracker';
import {
  CLICK_MANAGE_INVOICES,
  CLICK_MANAGE_PAYMENTS,
} from 'rich-text-lib/constants/usageTracking';
import { PM_INVOICE, PM_PAYMENT } from '../constants/events';

import * as PaymentLinksApi from '../commands/paymentLink/PaymentLinksApi';
import transformPaymentLinksData from '../commands/paymentLink/transformPaymentLinksData';
import PaymentPopoverEmptyState from '../commands/paymentLink/PaymentPopoverEmptyState';
import PaymentPopoverErrorState from '../commands/paymentLink/PaymentPopoverErrorState';
import { getPaymentLinkUrl } from '../commands/paymentLink/InsertPaymentLink';
import WrappedPaymentLinkItem from '../commands/paymentLink/WrappedPaymentLinkItem';
import { LinkPlugin } from '../plugins/link/LinkPlugin';

import type { InserterDefinition } from './types';

export const snippets: InserterDefinition = {
  insertionType: 'content',
  defaultOutputType: contentOutputs.EDITABLE,
  typeaheadConfig: {
    createApi: (apiClient, runtimeConfig) => {
      const wrapper = runtimeConfig.apiWrapper || SnippetsApi.defaultApiWrapper;
      const api = wrapper(apiClient);
      return {
        search: api.searchSnippets || SnippetsApi.searchSnippets,
        fetchRenderedContent: api.fetchRenderedContent,
        fetchUnrenderedContent: api.fetchUnrenderedContent,
      };
    },
    transformData: transformSnippetsData,
    EmptyState: SnippetsPopoverEmptyState,
    ErrorState: SnippetsPopoverErrorState,
    searchPlaceholder: 'draftPlugins.snippetsPlugin.popover.search.placeholder',
    titleMessage: 'draftPlugins.snippetsPlugin.popover.header.insertSnippet',
    manageUrl: (portalId) => getSnippetsUrl(portalId),
    getNewLinkUrl: getNewSnippetsUrl,
    contentKey: 'snippets',
  },
};

export const quotes: InserterDefinition = {
  insertionType: 'link',
  defaultOutputType: contentOutputs.RENDERED,
  typeaheadConfig: {
    createApi: (apiClient, runtimeConfig) => {
      const wrapper = runtimeConfig.apiWrapper || quotesApiWrapper;
      const api = wrapper(apiClient, runtimeConfig.contactVid);
      return {
        search: api.searchArticles,
        fetchRenderedContent: api.fetchRenderedContent,
      };
    },
    searchKey: (runtimeConfig) => runtimeConfig.contactVid,
    EmptyState: QuotesPopoverEmptyState,
    ErrorState: GenericPopoverErrorState,
    searchPlaceholder: 'draftPlugins.quotes.popover.search.searchPlaceholder',
    titleMessage: 'draftPlugins.quotes.popover.header.insertQuote',
    manageUrl: (portalId) => `${getFullUrl('app')}/quotes/${portalId}`,
    contentKey: 'quotes',
  },
};

export const templates: InserterDefinition = {
  insertionType: 'content',
  defaultOutputType: contentOutputs.RENDERED,
  onInsertContentUsage: 'metadata',
  typeaheadConfig: {
    createApi: (apiClient, runtimeConfig) => {
      const wrapper = runtimeConfig.apiWrapper || templatesApiWrapper;
      const api = wrapper(apiClient);
      return {
        search: api.searchTemplates,
        fetchRenderedContent: api.fetchRenderedContent,
        fetchUnrenderedContent:
          api.fetchUnrenderedContent ||
          templatesApiWrapper(apiClient).fetchUnrenderedContent,
      };
    },
    EmptyState: TemplatesPopoverEmptyState,
    ErrorState: TemplatesPopoverErrorState,
    searchPlaceholder: 'hubspot-prosemirror.generic.search.placeholder',
    titleMessage: 'hubspot-prosemirror.templates.title',
    manageUrl: getTemplatesUrl(),
    contentKey: 'templates',
  },
};

export const knowledgeArticle: InserterDefinition = {
  insertionType: 'link',
  defaultOutputType: contentOutputs.RENDERED,
  Renderer: KnowledgeArticleRenderer,
};

export const invoices: InserterDefinition = {
  insertionType: 'link',
  defaultOutputType: contentOutputs.RENDERED,
  typeaheadConfig: {
    createApi: (apiClient, __runtimeConfig) => {
      const api = InvoicesApi.defaultApiWrapper(apiClient);
      return {
        search: api.searchInvoices,
        fetchRenderedContent: api.fetchRenderedContent,
      };
    },
    transformData: transformInvoicesData,
    EmptyState: InvoicesPopoverEmptyState,
    ErrorState: InvoicesPopoverErrorState,
    searchPlaceholder:
      'hubspot-prosemirror.invoices.popover.search.placeholder',
    titleMessage: 'hubspot-prosemirror.invoices.popover.header.insertInvoice',
    manageUrl: getInvoicesUrl,
    contentKey: 'invoices',
    onClickManageButton: () =>
      Tracker.track(PM_INVOICE, { action: CLICK_MANAGE_INVOICES }),
  },
  onInsertContentUsage: 'tracking',
};

export const paymentLink: InserterDefinition = {
  insertionType: 'link',
  defaultOutputType: contentOutputs.RENDERED,
  createTransactionTransform: (runtimeConfig) => {
    const referrer = runtimeConfig.referrerParam;
    if (!referrer) return undefined;
    return (tr) => tr.setMeta(LinkPlugin, { paymentLinkReferrer: referrer });
  },
  typeaheadConfig: {
    createApi: (apiClient, runtimeConfig) => {
      const api = PaymentLinksApi.defaultApiWrapper(
        apiClient,
        runtimeConfig.referrerParam
      );
      return {
        search: api.searchLinks,
        fetchRenderedContent: api.fetchRenderedContent,
      };
    },
    transformData: transformPaymentLinksData,
    EmptyState: PaymentPopoverEmptyState,
    ErrorState: PaymentPopoverErrorState,
    searchPlaceholder:
      'hubspot-prosemirror.paymentLinks.popover.search.placeholder',
    titleMessage:
      'hubspot-prosemirror.paymentLinks.popover.header.insertPaymentLink',
    manageUrl: (portalId) => getPaymentLinkUrl(portalId),
    contentKey: 'paymentLinks',
    CustomItemComponent: WrappedPaymentLinkItem,
    onClickManageButton: () =>
      Tracker.track(PM_PAYMENT, { action: CLICK_MANAGE_PAYMENTS }),
  },
  onInsertContentUsage: 'tracking',
};

/**
 * Registry mapping capability keys to their inserter definitions.
 * Used by InsertMenu to render GenericInserter for each type.
 */
export const INSERTER_DEFINITIONS: Record<string, InserterDefinition> = {
  SNIPPETS: snippets,
  QUOTES: quotes,
  TEMPLATES: templates,
  KNOWLEDGE_ARTICLE: knowledgeArticle,
  INVOICES: invoices,
  PAYMENT_LINK: paymentLink,
};
